<script setup lang="ts">
import {DashboardWidgetProps} from "@/utils/presenters/interfaces.ts";
import {computed} from "vue";
import FullCalendar from '@fullcalendar/vue3'
import {CalendarOptions, EventClickInfo, EventInput} from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/vue3/daygrid";
import timeGridPlugin from "@fullcalendar/vue3/timegrid";
import interactionPlugin from "@fullcalendar/vue3/interaction";
import resourceTimelinePlugin from "@fullcalendar/vue3-scheduler/resource-timeline";
import type {ResourceInput} from "@fullcalendar/vue3-scheduler";
import {formaCalendarOptions, useCalendarDarkMode} from "@/utils/calendar/FullCalendarForma.ts";
import DashboardUtils from "@/utils/common/DashboardUtils.ts";
import Translator from "@/utils/common/Translator.ts";
import DashboardConstants from "@/utils/constants/DashboardConstants.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";

const props = defineProps<DashboardWidgetProps>();
const widgetId: string = (props.chartLine.widgetId ?? 'StackedAndGrouped');
const darkMode = useCalendarDarkMode();

let parameters: Record<any, any> = props.chartLine.parameters ?? {};
let minTime: string = parameters[DashboardConstants.Param_minTime] || "00:00:00";
let maxTime: string = parameters[DashboardConstants.Param_maxTime] || "24:00:00";
let openLinkInNewTab: boolean = parameters[DashboardConstants.Param_OpenLinkInNewTab] ?? false;

function createCalendarWithSchedulerIfNeeded(isScheduler: boolean): CalendarOptions {
  let events: EventInput[] = [];
  let resources: ResourceInput [] = [];
  DashboardUtils.createEventsAndResources(props.chartLine, events, resources);
  if (isScheduler) {
    resources = DashboardUtils.makeATree(resources);
    return {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, resourceTimelinePlugin],
      scrollTime: '00:00', // undo default 6am scrollTime
      locale: Translator.isArabic() ? "ar" : "en",
      direction: Translator.isArabic() ? "rtl" : "ltr",
      height: "100%",
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth'
      },
      initialView: 'resourceTimelineMonth',
      views: {
        timelineThreeDays: {
          type: 'resourceTimeline',
          duration: {days: 3}
        }
      },
      resourceColumnHeaderContent: "Resources",
      resources: resources,
      events: events,
      schedulerLicenseKey: 'AGPL-My-Frontend-And-Backend-Are-Open-Source',
      slotMinTime: minTime,
      slotMaxTime: maxTime,
      eventClick: function (e) {
        const editPlace = e.event.extendedProps.context?.editPlace;
        if (!editPlace)
          return;
        if (openLinkInNewTab) {
          e.jsEvent.preventDefault();
          window.open(PlaceUtils.linkToPlace(editPlace), "_blank");
        } else {
          DashboardUtils.handleReferenceQuickEdit(PlaceUtils.linkToPlace(editPlace), e.jsEvent);
        }
        closeMorePopover(e);
      }
    }
  } else {
    return {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      editable: false,
      dayMaxEvents: true, // allow "more" link when too many events
      locale: Translator.isArabic() ? "ar" : "en",
      direction: Translator.isArabic() ? "rtl" : "ltr",
      height: "100%",
      initialView: "dayGridMonth",
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      },
      events: events,
      eventClick: (e) => {
        const editPlace = e.event.extendedProps.context?.editPlace;
        if (!editPlace)
          return;
        if (openLinkInNewTab) {
          e.jsEvent.preventDefault();
          window.open(PlaceUtils.linkToPlace(editPlace), "_blank");
        } else {
          DashboardUtils.handleReferenceQuickEdit(PlaceUtils.linkToPlace(editPlace), e.jsEvent);
        }
        closeMorePopover(e);
      },
      slotMinTime: minTime,
      slotMaxTime: maxTime,
    }
  }
}

function closeMorePopover(e: EventClickInfo) {
  const morePopover = e.el.closest("[role=dialog][data-date]")
  if (!morePopover)
    return;
  morePopover.remove();
}

const calendarOptions = computed<CalendarOptions>(
  () => formaCalendarOptions(createCalendarWithSchedulerIfNeeded(props.isScheduler), darkMode.value));
</script>

<template>
  <div :key="chartKey + widgetId" class="tw-h-full tw-w-full">
    <FullCalendar :options="calendarOptions" class="nama-fc-sm tw-h-full tw-w-full"/>
  </div>
</template>
